import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import type { Map as MlMap } from 'maplibre-gl';
import { useNavigate } from '@tanstack/react-router';
import { Button, InlineNote, useNote } from '../components/ui';
import { MapProvider, MapView } from '../components/MapView';
import { Screen } from '../components/Screen';
import { useApp } from '../lib/app';
import { finishLater } from '../lib/live';
import { useRide } from '../lib/ride';
import { endedMessage, screenFor } from '../lib/rideScreen';
import { ensureLink, saveLink, savedLink, shareText, shouldAutoShare, trackUrl } from '../lib/share';
import type { Contact, ShareLink } from '../lib/types';
import { words } from '../lib/vehicles';
import { friendly } from '../lib/errors';
import { useT } from '../lib/i18n';
import { CancelSheet } from './CancelSheet';
import { Arrived } from './Arrived';
import { Arriving } from './Arriving';
import { Finding } from './Finding';
import { OnTrip } from './OnTrip';

/** The ride in progress: one route that shows the screen for the ride's status, live. */
export function LiveRide() {
  const { api, rider, setDraft } = useApp();
  const [map, setMap] = useState<MlMap | null>(null);
  const [shareNote, say] = useNote(6000);
  const { ride, loaded, setRide, refetch } = useRide();
  const t = useT();
  const nav = useNavigate();
  const [cancelling, setCancelling] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [link, setLink] = useState<(ShareLink & { auto?: boolean }) | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);

  // no ride: back home — unless the rider is leaving on purpose (Done → Your trips, Book again → Choose ride),
  // whose own navigation must win over this one
  const leaving = useRef(false);
  useEffect(() => {
    if (loaded && !ride && !leaving.current) nav({ to: '/home', replace: true });
  }, [loaded, ride, nav]);
  const leave = (to: '/trips' | '/choose' | '/home') => {
    leaving.current = true;
    nav({ to, replace: true });
    setRide(null);
  };
  useEffect(() => {
    api.contacts().then(setContacts, () => undefined);
  }, [api]);
  // this tab has shown this ride: Back to Home keeps it as a card there instead of opening it again
  useEffect(() => {
    if (!ride) return;
    try {
      sessionStorage.setItem('zr-opened', ride.id);
    } catch {
      /* ignore */
    }
  }, [ride?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setLink(ride ? savedLink(ride.id) : null);
  }, [ride?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // after dark, with sharing on and someone to share with: make the live link as soon as a driver is coming
  const screen = ride ? screenFor(ride.status) : null;
  // a status change (finding → driver coming → on the trip) morphs the sheet to its new size and crossfades its
  // content in a View Transition where the browser has one; the map stays live and glides to the new framing
  const [shown, setShown] = useState(screen);
  useEffect(() => {
    if (screen === shown) return;
    const go = () => flushSync(() => setShown(screen));
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const doc = document as Document & { startViewTransition?: (cb: () => void) => unknown };
    if (doc.startViewTransition && !reduce && shown) doc.startViewTransition(go);
    else go();
  }, [screen, shown]);
  useEffect(() => {
    if (!ride || !shouldAutoShare({ sharingOn: !!rider?.shareTripsAfterDark, contacts: contacts.length, screen, hasLink: !!savedLink(ride.id) })) return;
    ensureLink(api, ride.id, true).then(setLink, () => undefined);
  }, [api, ride, rider?.shareTripsAfterDark, contacts.length, screen]);

  if (!ride)
    // reading the ride (a refresh or a reopened tab): the sheet's outline while /rides/current answers
    return (
      <Screen name="finding-a-driver" kind="map" label={t('common.loading')}>
        <section className="zr-mapsheet" aria-busy="true" aria-label={t('common.loading')} style={{ height: 260 }}>
          <span className="zr-skeleton" style={{ marginTop: 28, height: 28, width: '62%', borderRadius: 10 }} />
          <span className="zr-skeleton" style={{ marginTop: 10, height: 18, width: '84%', borderRadius: 8 }} />
          <span className="zr-skeleton" style={{ marginTop: 22, height: 72, borderRadius: 20 }} />
        </section>
      </Screen>
    );

  const share = async () => {
    try {
      const l = await ensureLink(api, ride.id);
      setLink(l);
      const noun = words(t, ride.vehicleType).noun;
      const text = ride.driver?.firstName ? t('share.textWith', { noun, name: ride.driver.firstName }) : t('share.text', { noun });
      const r = await shareText(t('share.title'), text, trackUrl(l.token));
      say(r === 'copied' ? t('share.copied') : r === 'failed' ? trackUrl(l.token) : t('share.shared'), 'good');
    } catch (e) {
      say(friendly(e, t), 'error');
    }
  };
  const stopShare = async () => {
    if (!link) return;
    await api.unshare(ride.id, link.id).catch(() => undefined);
    saveLink(ride.id, null);
    setLink(null);
    say(t('share.stopped'));
  };
  // optimistic: the ride shows as cancelled at once; if the server says no, it comes back as it was
  const cancel = async (reason: string) => {
    setCancelOpen(false);
    setCancelling(true);
    const before = ride;
    setRide({ ...ride, status: 'CANCELLED_BY_RIDER', cancelReason: reason, endedAt: new Date().toISOString() });
    try {
      const r = await api.cancel(ride.id, reason);
      setRide(r);
    } catch (e) {
      setRide(before);
      say(friendly(e, t), 'error');
      await refetch();
    } finally {
      setCancelling(false);
    }
  };
  const askCancel = () => setCancelOpen(true);

  const note = <InlineNote note={shareNote} />;
  const view = shown ?? screen;
  if (view === 'finding' || view === 'arriving' || view === 'ontrip') {
    const scene = { map };
    const label = view === 'finding' ? t('find.sheet', { noun: words(t, ride.vehicleType).noun }) : view === 'arriving' ? t('arr.label') : t('trip.label');
    // one map for the whole ride: the sheet's content changes with the status, the map glides to the new framing
    return (
      <Screen name={view === 'finding' ? 'finding-a-driver' : view === 'arriving' ? 'driver-arriving' : 'on-the-trip'} label={label}>
        <MapView label={label} center={ride.pickup} zoom={15} onMap={setMap} />
        <MapProvider value={map}>
          <div key={view} style={{ display: 'contents' }}>
            {view === 'finding' && <Finding ride={ride} onCancel={askCancel} cancelling={cancelling} {...scene} />}
            {view === 'arriving' && <Arriving ride={ride} onCancel={askCancel} cancelling={cancelling} onShare={() => void share()} shareNote={note} {...scene} />}
            {view === 'ontrip' && <OnTrip ride={ride} link={link} onShare={() => void share()} onStopShare={() => void stopShare()} contactNames={contacts.map((c) => c.name)} shareNote={note} {...scene} />}
          </div>
        </MapProvider>
        <CancelSheet open={cancelOpen} ride={ride} onClose={() => setCancelOpen(false)} onConfirm={(r) => void cancel(r)} />
      </Screen>
    );
  }

  switch (view) {
    case 'arrived':
      return (
        <Arrived
          ride={ride}
          onSaved={setRide}
          onDone={() => {
            finishLater.close(ride.id);
            saveLink(ride.id, null);
            leave('/trips');
          }}
        />
      );
    default: {
      const msg = endedMessage(ride);
      const again = () => {
        setDraft({ pickup: ride.pickup, drop: ride.drop, quote: null, vehicleType: ride.vehicleType });
        leave('/choose');
      };
      return (
        <Screen name="finding-a-driver" kind="panel" label={t(msg.title)}>
          <div className="zr-fadein" style={{ position: 'absolute', left: 24, right: 24, top: 'calc(var(--sat) + 96px)' }}>
            <h1 style={{ margin: 0, fontSize: 32, lineHeight: '36px', fontWeight: 900, letterSpacing: '-0.03em' }}>{t(msg.title)}</h1>
            <p style={{ margin: '10px 0 0', fontSize: 16, lineHeight: '24px', fontWeight: 500, color: 'var(--ink2)' }}>{msg.body ? t(msg.body) : ''}</p>
          </div>
          <div style={{ position: 'absolute', left: 20, right: 20, bottom: 'calc(var(--sab) + 3px)', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {ride.status !== 'CANCELLED_BY_RIDER' && <Button onClick={again}>{t('ended.again')}</Button>}
            <Button
              variant="tonal"
              style={{ height: 56 }}
              onClick={() => leave('/home')}
            >
              {t('ended.home')}
            </Button>
          </div>
        </Screen>
      );
    }
  }
}
